import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  LayoutGrid,
  List,
  Mail,
  Phone,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useTechProfiles } from '@/hooks/use-crew';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { usePermissions } from '@/hooks/use-permissions';
import type { TechStatus, TechCertType, TechProfileListItem, TechCertStatus } from '@/lib/types';
import { CertificationHealthBadge } from '@/app/pages/crew/certification-health';

const statusColors: Record<TechStatus, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  on_leave: 'bg-yellow-100 text-yellow-800',
  terminated: 'bg-red-100 text-red-800',
  left: 'bg-red-100 text-red-800',
};

const statusLabels: Record<TechStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  on_leave: 'On Leave',
  terminated: 'Terminated',
  left: 'Left',
};

const certStatusColors: Record<TechCertStatus, string> = {
  valid: 'bg-green-100 text-green-800',
  expiring_soon: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
  revoked: 'bg-gray-100 text-gray-800',
};

export function TechsPage({ basePath = '/crew/techs' }: { basePath?: string } = {}) {
  const { canCreateTechs } = usePermissions();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [statusFilter, setStatusFilter] = useState<TechStatus | ''>(
    (searchParams.get('status') as TechStatus) || ''
  );
  const [certFilter, setCertFilter] = useState<TechCertType | ''>('');
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useTechProfiles({
    page,
    page_size: 20,
    q: debouncedSearch || undefined,
    status: statusFilter || undefined,
    cert_type: certFilter || undefined,
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">People</h1>
          <p className="text-gray-600 mt-1">
            {data ? `${data.count} team member${data.count !== 1 ? 's' : ''}` : 'Loading...'}
          </p>
        </div>
        {canCreateTechs && (
          <Button asChild>
            <Link to={`${basePath}/new`}>
              <Plus className="h-4 w-4 mr-2" />
              Add Employee
            </Link>
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, or job title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter || '_all'}
          onValueChange={(v) => {
            setStatusFilter(v === '_all' ? '' : (v as TechStatus));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="on_leave">On Leave</SelectItem>
            <SelectItem value="terminated">Terminated</SelectItem>
            <SelectItem value="left">Left</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={certFilter || '_all'}
          onValueChange={(v) => {
            setCertFilter(v === '_all' ? '' : (v as TechCertType));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="All Certs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Certs</SelectItem>
            <SelectItem value="sprat_1">SPRAT 1</SelectItem>
            <SelectItem value="sprat_2">SPRAT 2</SelectItem>
            <SelectItem value="sprat_3">SPRAT 3</SelectItem>
            <SelectItem value="irata_1">IRATA 1</SelectItem>
            <SelectItem value="irata_2">IRATA 2</SelectItem>
            <SelectItem value="irata_3">IRATA 3</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-1">
          <Button
            variant={viewMode === 'card' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('card')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'table' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('table')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : !data || data.results.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium">
              {debouncedSearch || statusFilter || certFilter ? 'No team members match your search' : 'No team members yet'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {debouncedSearch || statusFilter || certFilter
                ? 'Try adjusting your search or filters'
                : 'Add your first employee to start managing your crew'}
            </p>
            {canCreateTechs && (
              <Button asChild className="mt-4">
                <Link to={`${basePath}/new`}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add your first employee
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'card' ? (
        <TechCardGrid techs={data.results} basePath={basePath} />
      ) : (
        <TechTable techs={data.results} basePath={basePath} />
      )}

      {data && data.count > 20 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Page {page} of {Math.ceil(data.count / 20)}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function TechCardGrid({ techs, basePath }: { techs: TechProfileListItem[]; basePath: string }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {techs.map((tech) => (
        <Link key={tech.id} to={`${basePath}/${tech.id}`}>
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {tech.photo ? (
                    <img
                      src={tech.photo}
                      alt={tech.full_name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-lg">
                        {tech.first_name[0]}{tech.last_name[0]}
                      </span>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-gray-900 truncate">{tech.full_name}</h3>
                    <Badge className={statusColors[tech.status]}>
                      Employment: {statusLabels[tech.status]}
                    </Badge>
                    <CertificationHealthBadge
                      certifications={tech.certification_summary}
                      compact
                    />
                  </div>
                  {tech.job_title && (
                    <p className="text-sm text-gray-600 truncate">{tech.job_title}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                    {tech.email && (
                      <span className="flex items-center gap-1 truncate">
                        <Mail className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{tech.email}</span>
                      </span>
                    )}
                  </div>
                  {tech.phone && (
                    <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                      <Phone className="h-3 w-3 flex-shrink-0" />
                      <span>{tech.phone}</span>
                    </div>
                  )}
                  {tech.certification_summary && tech.certification_summary.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {tech.certification_summary.slice(0, 3).map((cert, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className={`text-xs ${certStatusColors[cert.status] ?? ''}`}
                        >
                          {cert.cert_name}
                        </Badge>
                      ))}
                      {tech.certification_summary.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{tech.certification_summary.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

function TechTable({ techs, basePath }: { techs: TechProfileListItem[]; basePath: string }) {
  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="hidden sm:table-cell">Job Title</TableHead>
            <TableHead>Employment</TableHead>
            <TableHead>Certification Status</TableHead>
            <TableHead className="hidden md:table-cell">Certifications</TableHead>
            <TableHead className="hidden lg:table-cell">Email</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {techs.map((tech) => (
            <TableRow key={tech.id} className="cursor-pointer hover:bg-gray-50">
              <TableCell>
                <Link to={`${basePath}/${tech.id}`} className="flex items-center gap-2">
                  {tech.photo ? (
                    <img
                      src={tech.photo}
                      alt={tech.full_name}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-xs">
                        {tech.first_name[0]}{tech.last_name[0]}
                      </span>
                    </div>
                  )}
                  <span className="font-medium">{tech.full_name}</span>
                </Link>
              </TableCell>
              <TableCell className="hidden sm:table-cell text-gray-600">
                {tech.job_title || '-'}
              </TableCell>
              <TableCell>
                <Badge className={statusColors[tech.status]}>
                  Employment: {statusLabels[tech.status]}
                </Badge>
              </TableCell>
              <TableCell>
                <CertificationHealthBadge
                  certifications={tech.certification_summary}
                  compact
                />
              </TableCell>
              <TableCell className="hidden md:table-cell">
                <div className="flex flex-wrap gap-1">
                  {tech.certification_summary?.slice(0, 2).map((cert, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className={`text-xs ${certStatusColors[cert.status] ?? ''}`}
                    >
                      {cert.cert_name}
                    </Badge>
                  ))}
                  {(tech.certification_summary?.length ?? 0) > 2 && (
                    <Badge variant="outline" className="text-xs">
                      +{(tech.certification_summary?.length ?? 0) - 2}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="hidden lg:table-cell text-gray-600">
                {tech.email}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
